import { DeviceState, RootState, Device } from '@/types';
import { GetterTree, MutationTree, ActionTree, Module } from 'vuex';
import DeviceService from '@/services/device.service';
import { DeviceMutator } from '@/mutators/device.mutator';
import store from '../store';
import { chunk } from 'lodash';
import EventBus from '@/services/eventbus.service';

type DeviceGetter = GetterTree<DeviceState, RootState>;

export const initialState = (): DeviceState => {
  return {
    initialState,
    devices: [],
    filters: {},
    total: 0,
  };
};

export const getters: DeviceGetter = {
  devices: (state) => state,
  deviceFilters: (state) => state.filters,
  devicesTabular: (state) => (config: any) => state.devices.map((device) => DeviceMutator.toTabular(device, config)),
  getDeviceById: (state) => (id: number) => state.devices.find((item) => item.id === Number(id)),
};

export const mutations: MutationTree<DeviceState> = {
  addDevices(state, newDevices) {
    state.total = newDevices.total;
    state.devices = newDevices.devices;
    state.filters = newDevices.columnFilters;
  },
  updateDevice(state, updatedDevice: Device) {
    state.devices[state.devices.findIndex((device: Device) => device.id === updatedDevice.id)] = updatedDevice;
  },
  updateDevices(state, updatedDevices: Device[]) {
    updatedDevices.map((updatedDevice: Device) => {
      state.devices[state.devices.findIndex((device: Device) => device.id === updatedDevice.id)] = updatedDevice;
    });
  },
  removeDevices(state, deviceIds) {
    deviceIds.map((deviceId: number) => {
      const idx = state.devices.findIndex((device: Device) => device.id === deviceId);
      if (idx >= 0) {
        state.devices.splice(idx, 1);
      }
    });
  },
};

export const actions: ActionTree<DeviceState, RootState> = {
  async addDevicesAsync(context: any, pagination: {
    take: number, skip: number, sort: string, desc: boolean,
    filters: Record<string, Record<string, boolean>>, projectId: number, searchNames: string,
  }) {
    const response = await DeviceService.getDevices(
      pagination.take, pagination.skip, pagination.sort,
      pagination.desc, pagination.filters,
      pagination.projectId, pagination.searchNames,
    );
    context.commit('addDevices', response);
    return response;
  },
  async updateDeviceAsync(context: any, device: { id: number, payload: object }) {
    const updatedDevice = await DeviceService.updateDevice(device.id, device.payload);
    context.commit('updateDevice', updatedDevice);
    return updatedDevice;
  },
  async mountDeviceAsync(context: any, device: { id: number, payload: object }) {
    try {
      const response = await DeviceService.mountDevice(device.id, device.payload);
      if (response.result === 'ok') {
        context.commit('updateDevice', response.device);
        return { ...response.device, subscribed: true, sim_required: false, allocation_limit: false };
      } else if (response.result === 'cancelled') {
        return null;
      } else if (response.result === 'no_sub') {
        return { subscribed: false, sim_required: false, allocation_limit: true };
      } else if (response.result === 'no_sim') {
        return { subscribed: false, sim_required: true, allocation_limit: false };
      } else if (response.result === 'allocation_limit_reached') {
        return { subscribed: false, sim_required: false, allocation_limit: true };
      } else {
        throw new Error(`Unknown API response string ${response.result}`);
      }
    } catch (err) {
      store.dispatch('addToastErrorMessage', (err as Error).message);
      return null;
    }
  },
  async unmountDeviceAsync(context: any, params: { deviceId: number}) {
    const updatedDevice = await DeviceService.unmountDevice(params.deviceId);
    context.commit('updateDevice', updatedDevice);
    return updatedDevice;
  },
  async unmountDevicesAsync(context: any, deviceIds: number[]) {
    let countCompleted = 0;
    const updatedDevices: Device[] = [];
    try {
      const batches = chunk(deviceIds, 50);
      for (const ids of batches) {
        const devs = await DeviceService.unmountDevices( ids );
        for (const dev of devs) {
          updatedDevices.push(dev);
        }
        countCompleted += ids.length;
      }
    } catch (e) {
      const err = e as Error;
      if (countCompleted === 0) {
        throw e;
      } else {
        throw new Error(`Failed to unmount ${deviceIds.length - countCompleted} out of ${deviceIds.length} devices. ${err.message}`);
      }
    } finally {
      if (updatedDevices.length) {
        context.commit('updateDevices', updatedDevices);
      }
    }
    return updatedDevices;
  },
  async unmountTestDeviceAsync(context: any, params: { deviceId: number}) {
    const updatedDevice = await DeviceService.unmountTestDevice(params.deviceId);
    context.commit('updateDevice', updatedDevice);
    return updatedDevice;
  },
  async unmountTestDevicesAsync(context: any, deviceIds: number[]) {
    let countCompleted = 0;
    const updatedDevices: Device[] = [];
    try {
      const batches = chunk(deviceIds, 50);
      for (const ids of batches) {
        const devs = await DeviceService.unmountTestDevices( ids );
        for (const dev of devs) {
          updatedDevices.push(dev);
        }
        countCompleted += ids.length;
      }
    } catch (e) {
      const err = e as Error;
      if (countCompleted === 0) {
        throw e;
      } else {
        throw new Error(`Failed to unmount ${deviceIds.length - countCompleted} out of ${deviceIds.length} devices. ${err.message}`);
      }
    } finally {
      if (updatedDevices.length) {
        context.commit('updateDevices', updatedDevices);
      }
    }
    return updatedDevices;
  },
  async assignDevicesAsync(context: any, payload: any) {
    let countCompleted = 0;
    const updatedDevices: Device[] = [];
    try {
      const batches = chunk(payload.deviceIds, 50);
      for (const ids of batches) {
        const devs = await DeviceService.assignDevices( {deviceIds: ids, projectId: payload.projectId} );
        for (const dev of devs) {
          updatedDevices.push(dev);
        }
        countCompleted += ids.length;
      }
    } catch (e) {
      const err = e as Error;
      if (countCompleted === 0) {
        throw e;
      } else {
        throw new Error(`Failed to Assign ${payload.deviceIds.length - countCompleted} out of ${payload.deviceIds.length} devices. ${err.message}`);
      }
    } finally {
      if (updatedDevices.length) {
        context.commit('updateDevices', updatedDevices);
      }
    }
    return updatedDevices;
  },
  async assignDevicesBulkAsync(context: any, payload: any) {
    const countCompleted = 0;
    try {
      const {job_id} = await DeviceService.assignDevicesBulk( payload );
      EventBus.$emit('jobCreated', job_id);
    } catch (e) {
      const err = e as Error;
      if ( countCompleted === 0 ) {
        throw e;
      } else {
        throw new Error( `Failed to Assign ${payload.deviceIds.length - countCompleted} out of ${payload.deviceIds.length} devices. ${err.message}` );
      }
    }
  },

  async unAssignDevicesAsync(context: any, params: {deviceIds: number[], unMountOnly: boolean}) {
    const payload: any[] = params.deviceIds.map((id) =>
      ({id: Number(id), unAssign: !params.unMountOnly, unMount: params.unMountOnly}));
    let countCompleted = 0;
    const updatedDevices: Device[] = [];
    try {
        const batches = chunk(payload, 50);
        for (const ids of batches) {
          const devs = await DeviceService.unAssignDevices( ids );
          for (const dev of devs) {
            updatedDevices.push(dev);
          }
          countCompleted += ids.length;
        }
      } catch (e) {
        const err = e as Error;
        if (countCompleted === 0) {
          throw e;
        } else {
          throw new Error(`Failed to unassign ${payload.length - countCompleted} out of ${payload.length} devices. ${err.message}`);
        }
      } finally {
        if (updatedDevices.length) {
          context.commit('updateDevices', updatedDevices);
        }
      }
    return updatedDevices;
  },

  async disposeDevicesAsync(context: any, payload: { deviceIds: number[], deleteReason: string }) {
    let countCompleted = 0;
    const updatedDevices: Device[] = [];
    try {
      const batches = chunk(payload.deviceIds, 50);
      for (const ids of batches) {
        const devs = await DeviceService.deleteDevices( ids, payload.deleteReason);
        countCompleted += ids.length;
      }
    } catch (e) {
      const err = e as Error;
      if (countCompleted === 0) {
        throw e;
      } else {
        throw new Error(`Failed to dispose ${payload.deviceIds.length - countCompleted} out of ${payload.deviceIds.length} devices. ${err.message}`);
      }
    } finally {
        context.commit('removeDevices', updatedDevices);
    }
    return updatedDevices;
  },
  async checkUniqueAsync(context: any, payload: {custom_id?: string, device_pac?: string, device_id?: string, bearer: string}) {
    return DeviceService.checkUnique(payload.custom_id, payload.device_id, payload.bearer);
  },
  async checkManyUniqueAsync(context, payload: any[]) {
    return DeviceService.checkManyUnique(payload);
  },
  async changeDeviceTypeAsync(context, payload: { deviceId: number, deviceVersionId: number}) {
    const updatedDevice = await DeviceService.changeDeviceType(payload.deviceId, payload.deviceVersionId);
    context.commit('updateDevice', updatedDevice);
    return updatedDevice;
  },

  async testDevicesAsync(context: any, deviceIds: number[]) {
    let countCompleted = 0;
    const updatedDevices: Device[] = [];
    try {
      const batches = chunk(deviceIds, 50);
      for (const ids of batches) {
        const devs = await DeviceService.testDevices( ids );
        for (const dev of devs) {
          updatedDevices.push(dev);
        }
        countCompleted += ids.length;
      }
    } catch (e) {
      const err = e as Error;
      if (countCompleted === 0) {
        throw e;
      } else {
        throw new Error(`Failed to mount ${deviceIds.length - countCompleted} out of ${deviceIds.length} devices. ${err.message}`);
      }
    } finally {
      if (updatedDevices.length) {
        context.commit('updateDevices', updatedDevices);
      }
    }
    return updatedDevices;
  },
  async unpairSimsAsync(context: any, deviceIds: number[]) {
    let countCompleted = 0;
    const updatedDevices: Device[] = [];
    try {
      const batches = chunk(deviceIds, 50);
      for (const ids of batches) {
        const devs = await DeviceService.unpairSims( ids );
        for (const dev of devs) {
          updatedDevices.push(dev);
        }
        countCompleted += ids.length;
      }
    } catch (e) {
      const err = e as Error;
      if (countCompleted === 0) {
        throw e;
      } else {
        throw new Error(`Failed to unpair ${deviceIds.length - countCompleted} out of ${deviceIds.length} devices. ${err.message}`);
      }
    } finally {
      if (updatedDevices.length) {
        context.commit('updateDevices', updatedDevices);
      }
    }
    return updatedDevices;
  },
};

export const devices: Module<DeviceState, RootState> = {
  state: initialState(),
  getters,
  mutations,
  actions,
};
